import React, {useMemo} from "react";
import {Typography} from "antd";
import {useTranslation} from "react-i18next";
import {observer} from "mobx-react-lite";
import {useStore} from "../../../../hooks/useStore";
import {useFetch} from "../../../../hooks/useFetch";
import {EmptyMessage} from "../../../_defaultComponents/EmptyMessage/EmptyMessage";
import {LinkOutlined} from "@ant-design/icons";
import {PLATFORM_COLORS, PLATFORM_GRADIENTS, PLATFORM_NAMES} from "../../../../constants/constants";
import {
    IntegrationsGrid,
    IntegrationBox,
    IntegrationIcon,
    IntegrationText,
    EmptyStateContainer,
    EmptyStateIcon,
    IntegrationTableContainer,
    ExternalLinkOutlined,
    IntegrationLinkContainer
} from "./style/CustomerIntegrationsTable.styles.js";
import externalLinkIcon from "../../../../public/images/external-link-icon.svg";

const {Text} = Typography;

export const CustomerIntegrationsTable = observer(() => {
    const {t} = useTranslation(["common"]);
    const stores = useStore();

    const selectedSubCompanyId = stores?.meStore?.selectedSubCompanyId;
    const company = stores?.meStore?.companies[0]._id;
    const companies = stores?.meStore?.selectedCompanies;
    const city = stores?.meStore?.selectedCity;

    const [subCompaniesLoading, subCompanies, setSubCompanies, setSubCompaniesLoading] = useFetch({
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            companyId: company,
            companies: companies?.join(".") || "",
            limit: 1000,
            city: !city || city === "all" ? undefined : city
        }),
        dependencies: [company, companies, city],
        initialCall: true,
        condition: company !== "pool"
    });

    // Seçili alt firmayı bul
    const selectedSubCompany = useMemo(() => {
        if (
            !subCompanies ||
            !subCompanies.rows ||
            !Array.isArray(subCompanies.rows) ||
            !selectedSubCompanyId
        ) {
            return null;
        }

        const found = subCompanies.rows.find(subCompany => subCompany._id === selectedSubCompanyId);
        return found;
    }, [subCompanies, selectedSubCompanyId]);

    // Eğer data yoksa veya seçili alt firma bulunamazsa boş mesaj göster
    if (
        !subCompaniesLoading &&
        (!selectedSubCompany || Object.keys(selectedSubCompany).length === 0)
    ) {
        return (
            <IntegrationTableContainer>
                <EmptyMessage />
            </IntegrationTableContainer>
        );
    }

    // Platform renklerini al
    const getPlatformColor = platform => {
        const upperPlatform = platform?.toUpperCase();
        return PLATFORM_COLORS?.[upperPlatform] || PLATFORM_COLORS.DEFAULT;
    };

    // Platform gradyanlarını al
    const getPlatformGradient = platform => {
        const upperPlatform = platform?.toUpperCase();
        return PLATFORM_GRADIENTS?.[upperPlatform] || PLATFORM_GRADIENTS?.DEFAULT;
    };

    return (
        <IntegrationTableContainer>
            {selectedSubCompany?.activeIntegrationUrls &&
            Array.isArray(selectedSubCompany.activeIntegrationUrls) &&
            selectedSubCompany.activeIntegrationUrls.length > 0 ? (
                <IntegrationsGrid>
                    {selectedSubCompany.activeIntegrationUrls.map((integration, index) => {
                        const platformColor = getPlatformColor(integration.platform);
                        const platformGradient = getPlatformGradient(integration.platform);

                        const handleClick = () => {
                            window.open(integration.url, "_blank");
                        };

                        return (
                            <IntegrationBox
                                key={index}
                                platformColor={platformColor}
                                platformGradient={platformGradient}
                                onClick={handleClick}
                                title={`${integration.platform.toUpperCase()} - ${integration.url}`}
                            >
                                <IntegrationText platformColor={platformColor}>
                                    {PLATFORM_NAMES?.[integration?.platform]}
                                </IntegrationText>
                                <IntegrationLinkContainer>
                                    Paneli ziyaret et.
                                    <ExternalLinkOutlined src={externalLinkIcon} />
                                </IntegrationLinkContainer>
                            </IntegrationBox>
                        );
                    })}
                </IntegrationsGrid>
            ) : (
                <EmptyStateContainer>
                    <Text type="secondary" style={{fontSize: "16px"}}>
                        Bu alt firma için entegrasyon bulunmamaktadır.
                    </Text>
                </EmptyStateContainer>
            )}
        </IntegrationTableContainer>
    );
});
